import type { StoredGuestSession } from "../types"

export const GUEST_SESSION_STORAGE_PREFIX = "wisefriend.meetingGuestSession"

export const isUuid = (value: string) => (
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
)

export const createGuestSessionId = () => {
  if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID()
  }
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (char) => (
    (Number(char) ^ ((Math.random() * 16) >> (Number(char) / 4))).toString(16)
  ))
}

export const getGuestSessionStorageKey = (meetingId?: string, guestToken?: string | null) => {
  if (!meetingId || !guestToken) return null
  return `${GUEST_SESSION_STORAGE_PREFIX}.${meetingId}.${guestToken}`
}

export const readStoredGuestSession = (meetingId?: string, guestToken?: string | null): StoredGuestSession | null => {
  const storageKey = getGuestSessionStorageKey(meetingId, guestToken)
  if (!storageKey || typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const displayName = String(parsed?.displayName || "").trim()
    const guestSessionId = String(parsed?.guestSessionId || "").trim()
    if (!displayName || !isUuid(guestSessionId)) return null
    return { displayName, guestSessionId, ...(typeof parsed.breakoutToken === "string" ? { breakoutToken: parsed.breakoutToken } : {}) }
  } catch {
    return null
  }
}

export const saveStoredGuestSession = (
  meetingId: string,
  guestToken: string,
  session: StoredGuestSession,
) => {
  const storageKey = getGuestSessionStorageKey(meetingId, guestToken)
  if (!storageKey || typeof window === "undefined") return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(session))
  } catch {
    // Guests can still join even if browser storage is unavailable.
  }
}

export const resolveGuestSession = (
  meetingId: string,
  guestToken: string,
  displayName: string,
  preferredSessionId?: string | null,
): StoredGuestSession => {
  const stored = readStoredGuestSession(meetingId, guestToken)
  const guestSessionId = isUuid(String(preferredSessionId || "")) ?
    String(preferredSessionId) :
    stored?.guestSessionId || createGuestSessionId()
  return {
    displayName: displayName.trim() || stored?.displayName || "Guest",
    guestSessionId,
    ...(stored?.guestSessionId === guestSessionId && stored.breakoutToken ? { breakoutToken: stored.breakoutToken } : {}),
  }
}
